import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Trash2,
  Loader2,
  Check,
  AlertTriangle,
  Plus,
  Calendar,
  Target,
  TrendingUp,
  Flag,
  Eye,
  Download,
  FileEdit,
  CheckCircle2,
  BarChart3,
  Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  getProjectWithDetails,
  updateProject,
  deleteProject,
  promoteProjectToActive,
  createMilestone,
  updateMilestone,
  deleteMilestone,
  createRisk,
  updateRisk,
  deleteRisk,
  createKpi,
  updateKpi,
  deleteKpi,
  recordKpiHistory,
  updateKpiHistoryPoint,
  deleteKpiHistoryPoint,
  createStatusUpdate,
  updateStatusUpdate,
  deleteStatusUpdate,
  getWorkspaceMembers,
  getProjectKpiHistory,
} from '@/lib/database';
import type { MemberWithEmail } from '@/types';
import type { KpiHistoryPoint } from './ProjectDashboard';
import type { Database, Tables } from '@/types/database.types';
import { useWorkspace } from '@/store/workspace';
import { useFeatureGate } from '@/hooks/useFeatureGate';
import { UpgradePrompt } from '@/components/subscription/UpgradePrompt';
import { ProjectDocumentsSection } from '@/components/documents/ProjectDocumentsSection';
import { ProjectDashboard } from './ProjectDashboard';
import { getProjectTasks, deriveCompletionFromTasks, type TaskRow } from '@/lib/db/tasks';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { ChevronDown } from 'lucide-react';
import { EXPORT_FORMATS } from '@/lib/export/registry';
import { generateExecutiveSummary } from '@/lib/llm';
import { track } from '@/lib/posthog';
import { draftDisabled } from '@/lib/draftRestrictions';
import { useActionBarSetter } from '@/contexts/ActionBarContext';
import { bumpProjectsVersion } from '@/store/projectsRefresh';
import { SaveStatus } from './SaveStatus';
import type { SaveStatusValue } from './SaveStatus';
import { healthOptions } from './constants';
import { MilestoneItem } from './MilestoneItem';
import { RiskItem } from './RiskItem';
import { StatusUpdateItem } from './StatusUpdateItem';
import { KpiItem, type KpiUpdates } from './KpiItem';
import { ProjectTasks } from './ProjectTasks';
import { RegenerateProjectDialog } from './RegenerateProjectDialog';

type MilestoneRow = Tables<'milestones'>;
type RiskRow = Tables<'risks'>;
type KpiRow = Tables<'kpis'>;
type StatusUpdateRow = Tables<'status_updates'>;
type ProjectStatus = Database['public']['Enums']['project_status'];

type Tab = 'dashboard' | 'details' | 'documents';

// Remember the user's last-chosen project tab across reloads and across
// switching from one project to another. Stored globally (not per project)
// so navigating project → project keeps you on the same tab. Read-only
// projects only expose the dashboard, but that fallback never overwrites
// this preference — see the access effect below.
const TAB_STORAGE_KEY = 'ingesta:project-active-tab';

function readStoredTab(): Tab {
  try {
    const stored = window.localStorage.getItem(TAB_STORAGE_KEY);
    if (stored === 'dashboard' || stored === 'details' || stored === 'documents') {
      return stored;
    }
  } catch {
    // localStorage unavailable (private mode / SSR / tests) — fall through.
  }
  return 'details';
}

function writeStoredTab(tab: Tab): void {
  try {
    window.localStorage.setItem(TAB_STORAGE_KEY, tab);
  } catch {
    // Ignore write failures — persistence is best-effort.
  }
}

export function ProjectDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { canEdit, currentWorkspace } = useWorkspace();
  const { canCreateProject, currentProjectCount, projectLimit } = useFeatureGate();
  const isReadOnly = !canEdit();
  // Set when this page is reached via the create-project flow's onCreated
  // navigation. Captured at first mount so subsequent re-renders don't lose
  // it (location.state itself persists across renders for the same history
  // entry, but capturing into a ref makes intent explicit).
  const fromCreateRef = useRef<boolean>(
    Boolean((location.state as { fromCreate?: boolean } | null)?.fromCreate)
  );
  const fromCreate = fromCreateRef.current;

  // UI state
  const [loading, setLoading] = useState(true);
  const [projectNotFound, setProjectNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>(() =>
    isReadOnly ? 'dashboard' : readStoredTab()
  );

  // Re-resolve the tab when the project changes (React Router reuses this
  // component across project → project navigation, so there's no remount) or
  // when access resolves. Read-only projects only expose the dashboard;
  // otherwise restore the saved preference. Deliberately does NOT persist —
  // a read-only project must not clobber the user's preferred tab.
  useEffect(() => {
    setActiveTab(isReadOnly ? 'dashboard' : readStoredTab());
  }, [id, isReadOnly]);

  // User-initiated tab change: update state and remember the choice.
  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    writeStoredTab(tab);
  };

  // Set when a dashboard section header is clicked: switch to the Details tab
  // and scroll to the matching section once it has rendered.
  const [pendingScrollSection, setPendingScrollSection] = useState<string | null>(null);
  const navigateToDetailsSection = (section: string) => {
    handleTabChange('details');
    setPendingScrollSection(section);
  };
  useEffect(() => {
    if (!pendingScrollSection || activeTab !== 'details') return;
    const target = pendingScrollSection;
    // Wait a frame so the Details DOM is mounted before scrolling to it.
    const raf = requestAnimationFrame(() => {
      document
        .getElementById(`section-${target}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    setPendingScrollSection(null);
    return () => cancelAnimationFrame(raf);
  }, [pendingScrollSection, activeTab]);
  const [saveStatus, setSaveStatus] = useState<SaveStatusValue>('idle');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDiscardDraftDialog, setShowDiscardDraftDialog] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [isPromoting, setIsPromoting] = useState(false);
  const [projectStatus, setProjectStatus] = useState<ProjectStatus>('active');

  // Project data
  const [projectName, setProjectName] = useState('');
  const [trackerId, setTrackerId] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [executiveSummary, setExecutiveSummary] = useState('');
  const [aiConfidence, setAiConfidence] = useState<number | null>(null);
  const [overallHealth, setOverallHealth] = useState<'green' | 'yellow' | 'red'>('green');
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [completionPercentage, setCompletionPercentage] = useState<number | null>(null);

  // Executive summary editor state. Clicking Regenerate on the
  // Dashboard opens a dialog with the current summary on the left
  // (read-only reference) and an editable textarea on the right. The
  // user can click "Generate now" to populate the textarea with fresh
  // AI output, type their own summary from scratch, or edit AI output
  // before saving. `isRegeneratingSummary` mirrors the dialog's
  // status='generating' so the Dashboard button can show a spinner.
  type SummaryReviewStatus = 'idle' | 'generating' | 'error';
  const [isRegeneratingSummary, setIsRegeneratingSummary] = useState(false);
  const [summaryReviewOpen, setSummaryReviewOpen] = useState(false);
  const [summaryReviewStatus, setSummaryReviewStatus] = useState<SummaryReviewStatus>('idle');
  const [summaryReviewOldText, setSummaryReviewOldText] = useState('');
  const [summaryReviewNewText, setSummaryReviewNewText] = useState('');
  const [summaryReviewError, setSummaryReviewError] = useState<string | null>(null);

  // Related data
  const [milestones, setMilestones] = useState<MilestoneRow[]>([]);
  const [risks, setRisks] = useState<RiskRow[]>([]);
  const [kpis, setKpis] = useState<KpiRow[]>([]);
  const [accomplishments, setAccomplishments] = useState<StatusUpdateRow[]>([]);
  const [priorities, setPriorities] = useState<StatusUpdateRow[]>([]);
  // Tasks power the dashboard's Tasks summary. Loaded here (the dashboard is
  // a read-only summary); the Details tab's editor manages its own copy.
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [members, setMembers] = useState<MemberWithEmail[]>([]);
  const [kpiHistory, setKpiHistory] = useState<Map<string, KpiHistoryPoint[]>>(new Map());
  const [isExporting, setIsExporting] = useState(false);
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  // Bump to force the main project-load effect to re-run after an external
  // mutation (e.g. a regeneration applies its delta).
  const [reloadKey, setReloadKey] = useState(0);

  // Tracks the project id whose data is currently mirrored in form state.
  // Auto-save is gated on this matching the route id so navigating between
  // projects can never write the previous project's values into the new row.
  const loadedIdRef = useRef<string | null>(null);
  const savedTimeoutRef = useRef<NodeJS.Timeout>();

  // Load project data
  useEffect(() => {
    let cancelled = false;
    loadedIdRef.current = null;
    setLoading(true);
    setProjectNotFound(false);

    const loadProject = async () => {
      if (!id) return;

      try {
        const project = await getProjectWithDetails(id);
        if (cancelled) return;
        if (project) {
          setProjectName(project.name);
          setTrackerId(project.external_id || null);
          setDescription(project.description || '');
          setExecutiveSummary(project.executive_summary || '');
          setAiConfidence(project.ai_confidence ?? null);
          setOverallHealth((project.overall_health as typeof overallHealth) || 'green');
          setStartDate(project.start_date || null);
          setEndDate(project.end_date || null);
          setCompletionPercentage(project.completion_percentage);

          setMilestones(project.milestones);
          setRisks(project.risks);
          setKpis(project.kpis);
          setAccomplishments(project.statusUpdates.filter(s => s.type === 'accomplishment'));
          setPriorities(project.statusUpdates.filter(s => s.type === 'priority'));
          setProjectStatus(project.status);

          // Tasks live in a separate table; fetch them for the dashboard
          // summary. Best-effort — a failure here shouldn't break the page.
          try {
            const taskRows = await getProjectTasks(id);
            if (!cancelled) setTasks(taskRows);
          } catch (taskErr) {
            console.error('Failed to load tasks for dashboard:', taskErr);
          }

          loadedIdRef.current = id;
        } else {
          setProjectNotFound(true);
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to load project:', error);
        setProjectNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadProject();
    // Force re-run when reloadKey changes (e.g. after Regenerate apply).
    void reloadKey;

    return () => {
      cancelled = true;
    };
  }, [id, reloadKey]);

  // Fetch workspace members so the Dashboard tab's hero can render real
  // avatars. Independent of project loading — the member list is per-
  // workspace, so re-fetching only when the workspace changes is enough.
  // Failures are non-fatal (the avatar stack just stays empty).
  useEffect(() => {
    if (!currentWorkspace?.id) {
      setMembers([]);
      return;
    }
    let cancelled = false;
    getWorkspaceMembers(currentWorkspace.id)
      .then(({ members: m }) => {
        if (!cancelled) setMembers(m);
      })
      .catch((error) => {
        console.error('Failed to load workspace members for dashboard:', error);
        if (!cancelled) setMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [currentWorkspace?.id]);

  // Fetch KPI history so the dashboard sparklines have real data when
  // present. Existing KPIs that pre-date the kpi_history table will
  // simply have no history; the dashboard falls back to a [0, current]
  // line for those. Refetched on project change and whenever the kpis
  // list changes (e.g. after creating/updating a KPI in the Details
  // tab — kpis state is the dependency).
  useEffect(() => {
    if (!id || kpis.length === 0) {
      setKpiHistory(new Map());
      return;
    }
    let cancelled = false;
    getProjectKpiHistory(id)
      .then((history) => {
        if (!cancelled) setKpiHistory(history);
      })
      .catch((error) => {
        console.error('Failed to load KPI history:', error);
        if (!cancelled) setKpiHistory(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [id, kpis]);

  // Auto-save: debounce the entire save operation so all fields are written
  // together from a single, consistent snapshot. Per-field debouncing with a
  // full-record write produced mixed-state payloads (e.g. saving with a stale
  // empty name while health had updated) and corrupted projects.
  useEffect(() => {
    if (!id || loadedIdRef.current !== id || isReadOnly) return;

    const handler = setTimeout(async () => {
      if (loadedIdRef.current !== id) return;

      setSaveStatus('saving');
      try {
        await updateProject(id, {
          name: projectName,
          external_id: trackerId,
          description: description || null,
          executive_summary: executiveSummary || null,
          overall_health: overallHealth,
          start_date: startDate,
          end_date: endDate,
          completion_percentage: completionPercentage,
        });
        if (loadedIdRef.current !== id) return;
        // Notify any project lists (sidebar, /projects grid) that a project
        // changed so they re-render with the new name / etc.
        bumpProjectsVersion();
        setSaveStatus('saved');

        if (savedTimeoutRef.current) {
          clearTimeout(savedTimeoutRef.current);
        }
        savedTimeoutRef.current = setTimeout(() => {
          setSaveStatus('idle');
        }, 2000);
      } catch (error) {
        console.error('Failed to save project:', error);
        setSaveStatus('error');
      }
    }, 800);

    return () => clearTimeout(handler);
  }, [id, projectName, trackerId, description, executiveSummary, overallHealth, startDate, endDate, completionPercentage, isReadOnly]);

  // Overall completion derived from tasks. Once a project has active tasks,
  // task progress becomes the source of truth for its status: mark tasks
  // complete and the number moves. `null` means no active tasks, in which case
  // completion stays whatever was set manually / extracted (see the Completion
  // card below, which then shows an editable slider instead).
  const derivedCompletion = deriveCompletionFromTasks(tasks);

  // Push the derived value into completion state only when task progress
  // actually changes — not on every render — so it updates the number as tasks
  // are completed while still letting the user drag the slider to a manual
  // value that then sticks until the next task change. Feeds the same autosave
  // above (persisting so the home overview and exports stay in sync) and the
  // dashboard ring.
  const prevDerivedRef = useRef<number | null>(null);
  useEffect(() => {
    if (derivedCompletion === null) {
      prevDerivedRef.current = null;
      return;
    }
    if (derivedCompletion !== prevDerivedRef.current) {
      prevDerivedRef.current = derivedCompletion;
      setCompletionPercentage(derivedCompletion);
    }
  }, [derivedCompletion]);

  // Open the review dialog. Doesn't auto-generate — the user explicitly
  // chooses whether to invoke the LLM by clicking "Generate now" inside
  // the dialog. They can also just type a summary from scratch in the
  // editable textarea, or edit AI output before saving. The existing
  // executive summary isn't touched until the user clicks "Save changes".
  const handleRegenerateSummary = () => {
    setSummaryReviewOldText(executiveSummary);
    setSummaryReviewNewText('');
    setSummaryReviewError(null);
    setSummaryReviewStatus('idle');
    setSummaryReviewOpen(true);
  };

  // Triggered by the "Generate now" button inside the dialog. Replaces
  // whatever's in the editable textarea with fresh AI output.
  const generateSummaryNow = async () => {
    if (!id) return;
    setSummaryReviewStatus('generating');
    setSummaryReviewError(null);
    setIsRegeneratingSummary(true);
    try {
      const fresh = await generateExecutiveSummary(id);
      setSummaryReviewNewText(fresh);
      setSummaryReviewStatus('idle');
    } catch (err: unknown) {
      console.error('Failed to regenerate executive summary:', err);
      setSummaryReviewError(err instanceof Error ? err.message : 'Failed to regenerate summary');
      setSummaryReviewStatus('error');
    } finally {
      setIsRegeneratingSummary(false);
    }
  };

  // Save whatever's in the editable textarea — could be AI-generated,
  // user-typed, or a mix. Setting state triggers the auto-save effect
  // which persists alongside any other in-flight field changes.
  const saveSummaryChanges = () => {
    setExecutiveSummary(summaryReviewNewText);
    track('executive_summary_regenerated', { project_id: id });
    setSummaryReviewOpen(false);
  };

  const cancelSummaryReview = () => {
    setSummaryReviewOpen(false);
  };

  // Export pipeline. Each format in @/lib/export/registry takes the same
  // project-data shape and an extension-point context (currently empty).
  const handleExport = async (formatId: string) => {
    const format = EXPORT_FORMATS.find((f) => f.id === formatId);
    if (!format || !id) return;
    setIsExporting(true);
    try {
      track('project_exported', {
        project_id: id,
        format: formatId,
        milestone_count: milestones.length,
        risk_count: risks.length,
        kpi_count: kpis.length,
      });
      await format.run(
        {
          id,
          name: projectName,
          trackerId,
          description,
          overallHealth,
          startDate,
          endDate,
          completionPercentage,
          executiveSummary: executiveSummary || null,
          milestones,
          risks,
          kpis,
          tasks,
          accomplishments,
          priorities,
        },
        {}
      );
    } catch (err: unknown) {
      console.error('Export failed:', err);
      // Non-fatal: log to console for now. Surface to user via toast in a
      // follow-up — most failures are user-visible (download didn't
      // start) and re-trying usually works.
    } finally {
      setIsExporting(false);
    }
  };

  // Milestone handlers
  const handleAddMilestone = async () => {
    if (!id) return;
    setSaveStatus('saving');
    try {
      const newMilestone = await createMilestone(id, {
        name: 'New Milestone',
        status: 'not_started',
      });
      setMilestones([...milestones, newMilestone]);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to create milestone:', error);
      setSaveStatus('error');
    }
  };

  const handleUpdateMilestone = async (milestoneId: string, updates: Partial<{ name: string; dueDate: string | null; status: 'not_started' | 'in_progress' | 'completed' | 'overdue'; ownerName: string | null }>) => {
    setSaveStatus('saving');
    try {
      const updated = await updateMilestone(milestoneId, updates);
      setMilestones(milestones.map(m => m.id === milestoneId ? updated : m));
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to update milestone:', error);
      setSaveStatus('error');
    }
  };

  const handleDeleteMilestone = async (milestoneId: string) => {
    setSaveStatus('saving');
    try {
      await deleteMilestone(milestoneId);
      setMilestones(milestones.filter(m => m.id !== milestoneId));
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to delete milestone:', error);
      setSaveStatus('error');
    }
  };

  // Risk handlers
  const handleAddRisk = async () => {
    if (!id) return;
    setSaveStatus('saving');
    try {
      const newRisk = await createRisk(id, {
        summary: 'New Risk',
        severity: 'medium',
      });
      setRisks([newRisk, ...risks]);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to create risk:', error);
      setSaveStatus('error');
    }
  };

  const handleUpdateRisk = async (riskId: string, updates: Partial<{ summary: string; description: string | null; severity: 'low' | 'medium' | 'high' | 'critical'; status: 'open' | 'mitigating' | 'mitigated' | 'closed'; identifiedDate: string | null; ownerName: string | null; mitigationPlan: string | null }>) => {
    setSaveStatus('saving');
    try {
      const updated = await updateRisk(riskId, updates);
      setRisks(risks.map(r => r.id === riskId ? updated : r));
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to update risk:', error);
      setSaveStatus('error');
    }
  };

  // KPI handlers
  const handleAddKpi = async () => {
    if (!id) return;
    setSaveStatus('saving');
    try {
      const newKpi = await createKpi(id, { name: 'New KPI' });
      setKpis([...kpis, newKpi]);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to create kpi:', error);
      setSaveStatus('error');
    }
  };

  const handleUpdateKpi = async (kpiId: string, updates: KpiUpdates) => {
    setSaveStatus('saving');
    try {
      const updated = await updateKpi(kpiId, updates);
      setKpis(kpis.map(k => k.id === kpiId ? updated : k));
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to update kpi:', error);
      setSaveStatus('error');
    }
  };

  const handleDeleteKpi = async (kpiId: string) => {
    setSaveStatus('saving');
    try {
      await deleteKpi(kpiId);
      setKpis(kpis.filter(k => k.id !== kpiId));
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to delete kpi:', error);
      setSaveStatus('error');
    }
  };

  // KPI history handlers. Each one mutates the canonical kpi_history table
  // and then locally patches kpiHistory state so the dashboard sparkline
  // updates without a full refetch.
  const refreshKpiHistory = async () => {
    if (!id) return;
    try {
      const next = await getProjectKpiHistory(id);
      setKpiHistory(next);
    } catch (error) {
      console.error('Failed to refresh kpi history:', error);
    }
  };

  const handleAddKpiHistoryPoint = async (
    kpiId: string,
    value: number,
    recordedAt: string,
  ) => {
    setSaveStatus('saving');
    try {
      await recordKpiHistory(kpiId, value, recordedAt);
      await refreshKpiHistory();
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to add kpi history point:', error);
      setSaveStatus('error');
    }
  };

  const handleUpdateKpiHistoryPoint = async (
    _kpiId: string,
    historyId: string,
    value: number,
    recordedAt: string,
  ) => {
    setSaveStatus('saving');
    try {
      await updateKpiHistoryPoint(historyId, { value, recordedAt });
      await refreshKpiHistory();
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to update kpi history point:', error);
      setSaveStatus('error');
    }
  };

  const handleDeleteKpiHistoryPoint = async (
    _kpiId: string,
    historyId: string,
  ) => {
    setSaveStatus('saving');
    try {
      await deleteKpiHistoryPoint(historyId);
      await refreshKpiHistory();
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to delete kpi history point:', error);
      setSaveStatus('error');
    }
  };

  const handleDeleteRisk = async (riskId: string) => {
    setSaveStatus('saving');
    try {
      await deleteRisk(riskId);
      setRisks(risks.filter(r => r.id !== riskId));
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to delete risk:', error);
      setSaveStatus('error');
    }
  };

  // Status update handlers (accomplishments & priorities)
  const handleAddStatusUpdate = async (type: 'accomplishment' | 'priority') => {
    if (!id) return;
    setSaveStatus('saving');
    try {
      const newItem = await createStatusUpdate(id, {
        type,
        content: type === 'accomplishment' ? 'New accomplishment' : 'New priority',
      });
      if (type === 'accomplishment') {
        setAccomplishments([newItem, ...accomplishments]);
      } else {
        setPriorities([newItem, ...priorities]);
      }
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to create status update:', error);
      setSaveStatus('error');
    }
  };

  const handleUpdateStatusUpdate = async (itemId: string, content: string, type: 'accomplishment' | 'priority') => {
    setSaveStatus('saving');
    try {
      const updated = await updateStatusUpdate(itemId, { content });
      if (type === 'accomplishment') {
        setAccomplishments(accomplishments.map(a => a.id === itemId ? updated : a));
      } else {
        setPriorities(priorities.map(p => p.id === itemId ? updated : p));
      }
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to update status update:', error);
      setSaveStatus('error');
    }
  };

  const handleDeleteStatusUpdate = async (itemId: string, type: 'accomplishment' | 'priority') => {
    setSaveStatus('saving');
    try {
      await deleteStatusUpdate(itemId);
      if (type === 'accomplishment') {
        setAccomplishments(accomplishments.filter(a => a.id !== itemId));
      } else {
        setPriorities(priorities.filter(p => p.id !== itemId));
      }
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to delete status update:', error);
      setSaveStatus('error');
    }
  };

  // Delete project handler
  const handleDeleteProject = async () => {
    if (!id) return;

    const expectedText = `delete ${projectName}`;
    if (deleteConfirmation !== expectedText) {
      return;
    }

    try {
      setIsDeleting(true);
      await deleteProject(id);
      setShowDeleteDialog(false);
      navigate('/projects');
    } catch (error) {
      console.error('Failed to delete project:', error);
      setIsDeleting(false);
    }
  };

  // Promote a draft project to active. Gated by the same project-limit
  // check that blocks new project creation, since promotion is what actually
  // counts toward the workspace's project limit.
  const handlePromoteDraft = async () => {
    if (!id) return;
    if (!canCreateProject) {
      setShowUpgradePrompt(true);
      return;
    }
    setIsPromoting(true);
    try {
      await promoteProjectToActive(id);
      setProjectStatus('active');
      track('project_promoted_active', { project_id: id });
      // Sidebar / projects grid only render `active` projects; tell them
      // to refetch so the freshly-promoted project shows up.
      bumpProjectsVersion();
    } catch (error) {
      console.error('Failed to promote draft:', error);
    } finally {
      setIsPromoting(false);
    }
  };

  const handleDiscardDraft = async () => {
    if (!id) return;
    try {
      setIsDeleting(true);
      await deleteProject(id);
      setShowDiscardDraftDialog(false);
      navigate('/projects');
    } catch (error) {
      console.error('Failed to discard draft:', error);
      setIsDeleting(false);
    }
  };

  const isDraft = projectStatus === 'draft';

  // Publish a persistent bottom action bar while viewing a draft so the
  // primary actions (Set Active / Discard) are always one click away
  // without scrolling. Refs let the published bar always invoke the
  // latest handler closures without rerunning the publishing effect.
  const setActionBar = useActionBarSetter();
  const handlePromoteRef = useRef(handlePromoteDraft);
  const showDiscardRef = useRef(() => setShowDiscardDraftDialog(true));
  handlePromoteRef.current = handlePromoteDraft;
  showDiscardRef.current = () => setShowDiscardDraftDialog(true);

  useEffect(() => {
    if (!isDraft || isReadOnly) {
      setActionBar(null);
      return;
    }
    setActionBar(
      <div className="w-full max-w-7xl mx-auto flex items-center justify-between gap-3 px-8 py-3">
        <Button
          variant="outline"
          size="lg"
          onClick={() => showDiscardRef.current()}
          disabled={isPromoting || isDeleting}
        >
          <Trash2 className="h-4 w-4 mr-2" />
          {fromCreate ? 'Cancel' : 'Discard'}
        </Button>
        <Button
          size="lg"
          onClick={() => handlePromoteRef.current()}
          disabled={isPromoting || isDeleting}
        >
          {isPromoting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {fromCreate ? 'Creating…' : 'Setting Active…'}
            </>
          ) : (
            <>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              {fromCreate ? 'Create Project' : 'Set Project Active'}
            </>
          )}
        </Button>
      </div>
    );
    return () => setActionBar(null);
  }, [isDraft, isReadOnly, isPromoting, isDeleting, fromCreate, setActionBar]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (projectNotFound) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Project Not Found
        </h2>
        <p className="text-gray-600">
          The project you're looking for doesn't exist or you don't have access to it.
        </p>
      </div>
    );
  }

  if (showUpgradePrompt) {
    return (
      <div className="max-w-7xl mx-auto px-8 space-y-6">
        <UpgradePrompt
          type="project-limit"
          currentProjectCount={currentProjectCount}
          projectLimit={projectLimit}
          onDismiss={() => setShowUpgradePrompt(false)}
        />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-8 space-y-6">
      {/* Draft banner: informational only — actions live in the persistent
          bottom action bar (published below) so they're always reachable
          without scrolling. */}
      {isDraft && !isReadOnly && !fromCreate && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <FileEdit className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-900">This project is a draft.</p>
            <p className="text-sm text-amber-700">
              It's only visible to you. Set it active when you want it to count toward
              your active projects and become visible to other workspace members.
            </p>
          </div>
        </div>
      )}

      {/* Tabs + Save Status */}
      <div className="flex items-end justify-between border-b border-gray-200">
        {/* Tab nav. Read-only viewers see only the Dashboard tab — they
            can't edit Details, don't need the Documents editor, and
            shouldn't have access to internal scaffolding like the (Demo)
            tab. The Dashboard itself hides edit affordances (Regenerate
            etc.) when onRegenerateSummary is null, so a viewer's
            Dashboard is purely read-only. */}
        <nav className="flex gap-1 -mb-px" aria-label="Project sections">
          <button
            type="button"
            onClick={() => handleTabChange('dashboard')}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'dashboard'
                ? 'border-ingesta-blue-600 text-ingesta-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            )}
          >
            Dashboard
          </button>
          {!isReadOnly && (
            <>
              <button
                type="button"
                onClick={() => handleTabChange('details')}
                className={cn(
                  'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
                  activeTab === 'details'
                    ? 'border-ingesta-blue-600 text-ingesta-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                )}
              >
                Details
              </button>
              <button
                type="button"
                onClick={() => handleTabChange('documents')}
                className={cn(
                  'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
                  activeTab === 'documents'
                    ? 'border-ingesta-blue-600 text-ingesta-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                )}
              >
                Documents
              </button>
            </>
          )}
        </nav>
        <div className="flex items-center gap-2 pb-2">
          {isReadOnly ? (
            <div className="flex items-center gap-1.5 text-xs px-2 py-1 rounded-full text-gray-500 bg-gray-100">
              <Eye className="h-3 w-3" />
              <span>Read-only</span>
            </div>
          ) : (
            <SaveStatus status={saveStatus} />
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                {...draftDisabled(isDraft)}
                disabled={isExporting || isDraft}
              >
                <Download className="h-4 w-4 mr-1" />
                {isExporting ? 'Exporting…' : 'Export'}
                <ChevronDown className="h-4 w-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              {EXPORT_FORMATS.map((fmt) => (
                <DropdownMenuItem
                  key={fmt.id}
                  onClick={() => handleExport(fmt.id)}
                  className="flex flex-col items-start gap-0.5 py-2"
                >
                  <span className="text-sm font-medium">
                    {fmt.label} ({fmt.fileFormat})
                  </span>
                  {fmt.description && (
                    <span className="text-xs text-gray-500">{fmt.description}</span>
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {activeTab === 'dashboard' && (
        <ProjectDashboard
          project={{
            name: projectName,
            description: description || null,
            external_id: trackerId,
            overall_health: overallHealth,
            start_date: startDate,
            end_date: endDate,
            completion_percentage: completionPercentage,
            executive_summary: executiveSummary || null,
          }}
          members={members
            .filter((m): m is MemberWithEmail & { user_id: string } => m.user_id !== null)
            .map((m) => ({
              id: m.user_id,
              name: m.name,
              email: m.email,
            }))}
          kpis={kpis}
          kpiHistory={kpiHistory}
          tasks={tasks}
          milestones={milestones}
          risks={risks}
          priorities={priorities}
          accomplishments={accomplishments}
          isRegeneratingSummary={isRegeneratingSummary}
          onRegenerateSummary={isReadOnly ? null : handleRegenerateSummary}
          onNavigateToSection={isReadOnly ? undefined : navigateToDetailsSection}
        />
      )}

      {activeTab === 'details' && (
        <>
      {/* AI extraction banner — only shown on the first navigation here from
          the create flow (fromCreate) when the project has AI-extracted
          content (ai_confidence is set). Once the user navigates away or
          reloads, location.state clears and the banner stops showing. */}
      {fromCreate && aiConfidence !== null && (
        <div className="bg-ingesta-blue-50 border border-ingesta-blue-200 rounded-lg p-4 flex items-start gap-3">
          <Sparkles className="h-5 w-5 text-ingesta-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-ingesta-blue-900">
              AI-Extracted Data ({Math.round(aiConfidence * 100)}% confidence)
            </p>
            <p className="text-sm text-ingesta-blue-700 mt-1">
              Review and edit the extracted information below before setting this project active.
            </p>
          </div>
        </div>
      )}

      {/* Project Name & Description — first thing the user sees so the page
          identifies itself before any AI-generated narrative. */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex items-start gap-4">
            <div className="flex-1">
              <Input
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                className="text-xl font-semibold border-0 border-b rounded-none px-0 focus-visible:ring-0 focus-visible:border-ingesta-blue-500"
                placeholder="Project Name"
                readOnly={isReadOnly}
              />
            </div>
            <div className="w-40">
              <label className="text-xs text-gray-500 mb-1 block">Project ID / Tracker</label>
              <Input
                value={trackerId || ''}
                onChange={(e) => setTrackerId(e.target.value || null)}
                className="text-sm"
                placeholder="e.g., PRJ-001"
                readOnly={isReadOnly}
              />
            </div>
          </div>
          <div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full p-0 border-0 resize-none focus:outline-none text-gray-600 text-sm min-h-[60px] placeholder:text-gray-400"
              placeholder="Add a description for this project..."
              rows={2}
              readOnly={isReadOnly}
            />
          </div>
        </CardContent>
      </Card>

      {/* Executive summary lives on the Dashboard tab now — Regenerate
          opens a review dialog (rendered at the bottom of this component)
          and the accepted text auto-saves through the same effect that
          handles every other field. */}

      {/* Health & Dates */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Overall Health */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
              <Target className="h-4 w-4" />
              Overall Health
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              {healthOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => !isReadOnly && setOverallHealth(option.value)}
                  disabled={isReadOnly}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 rounded-lg border transition-all flex-1 justify-center',
                    overallHealth === option.value
                      ? `${option.bgLight} border-2`
                      : 'border-gray-200 hover:border-gray-300 bg-white',
                    isReadOnly && 'cursor-not-allowed opacity-60'
                  )}
                >
                  <span className={cn('w-2.5 h-2.5 rounded-full', option.color)} />
                  <span className="text-sm font-medium">{option.label}</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Dates */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Timeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="text-xs text-gray-500 mb-1 block">Start</label>
                <Input
                  type="date"
                  value={startDate || ''}
                  onChange={(e) => setStartDate(e.target.value || null)}
                  className="text-sm"
                  readOnly={isReadOnly}
                />
              </div>
              <div className="flex-1">
                <label className="text-xs text-gray-500 mb-1 block">End</label>
                <Input
                  type="date"
                  value={endDate || ''}
                  onChange={(e) => setEndDate(e.target.value || null)}
                  className="text-sm"
                  readOnly={isReadOnly}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Completion. Always an editable slider for editors — shown even when no
          value was ever set (null → 0) so the control is never hidden on a stuck
          0%. When the project has active tasks, task progress feeds the value
          automatically as tasks are completed, but the user can still drag to
          override. */}
      {(completionPercentage !== null || !isReadOnly) && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Completion
              </CardTitle>
              <span className="text-lg font-semibold text-ingesta-blue-600">
                {completionPercentage ?? 0}%
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <input
              type="range"
              min="0"
              max="100"
              value={completionPercentage ?? 0}
              onChange={(e) => setCompletionPercentage(parseInt(e.target.value))}
              className={cn(
                "w-full h-2 bg-gray-200 rounded-lg appearance-none accent-ingesta-blue-600",
                isReadOnly ? "cursor-not-allowed opacity-60" : "cursor-pointer"
              )}
              disabled={isReadOnly}
            />
            {derivedCompletion !== null && (
              <p className="mt-2 text-xs text-gray-500">
                Auto-updates from tasks ({tasks.filter((t) => t.status === 'complete').length} of{' '}
                {tasks.filter((t) => t.status !== 'cancelled').length} complete) — drag to override.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Milestones */}
      <Card id="section-milestones" className="scroll-mt-4">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Flag className="h-5 w-5 text-ingesta-blue-500" />
              Milestones
            </CardTitle>
            {!isReadOnly && (
              <Button variant="outline" size="sm" onClick={handleAddMilestone}>
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {milestones.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">
              No milestones yet.{!isReadOnly && ' Add one to track key deliverables.'}
            </p>
          ) : (
            <div className="space-y-3">
              {milestones.map((milestone) => (
                <MilestoneItem
                  key={milestone.id}
                  milestone={milestone}
                  onUpdate={handleUpdateMilestone}
                  onDelete={handleDeleteMilestone}
                  readOnly={isReadOnly}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Priorities */}
      <Card id="section-priorities" className="scroll-mt-4">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-500" />
              Priorities
            </CardTitle>
            {!isReadOnly && (
              <Button variant="outline" size="sm" onClick={() => handleAddStatusUpdate('priority')}>
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {priorities.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">No priorities set</p>
          ) : (
            <ul className="space-y-2">
              {priorities.map((item) => (
                <StatusUpdateItem
                  key={item.id}
                  item={item}
                  type="priority"
                  onUpdate={(content) => handleUpdateStatusUpdate(item.id, content, 'priority')}
                  onDelete={() => handleDeleteStatusUpdate(item.id, 'priority')}
                  readOnly={isReadOnly}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Risks */}
      <Card id="section-risks" className="scroll-mt-4">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Risks
            </CardTitle>
            {!isReadOnly && (
              <Button variant="outline" size="sm" onClick={handleAddRisk}>
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {risks.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">
              No risks identified.{!isReadOnly && ' Add any potential risks to track them.'}
            </p>
          ) : (
            <div className="space-y-3">
              {risks.map((risk) => (
                <RiskItem
                  key={risk.id}
                  risk={risk}
                  onUpdate={handleUpdateRisk}
                  onDelete={handleDeleteRisk}
                  readOnly={isReadOnly}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Accomplishments */}
      <Card id="section-accomplishments" className="scroll-mt-4">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Check className="h-5 w-5 text-green-500" />
              Accomplishments
            </CardTitle>
            {!isReadOnly && (
              <Button variant="outline" size="sm" onClick={() => handleAddStatusUpdate('accomplishment')}>
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {accomplishments.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">No accomplishments yet</p>
          ) : (
            <ul className="space-y-2">
              {accomplishments.map((item) => (
                <StatusUpdateItem
                  key={item.id}
                  item={item}
                  type="accomplishment"
                  onUpdate={(content) => handleUpdateStatusUpdate(item.id, content, 'accomplishment')}
                  onDelete={() => handleDeleteStatusUpdate(item.id, 'accomplishment')}
                  readOnly={isReadOnly}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* KPIs */}
      <Card id="section-kpis" className="scroll-mt-4">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-ingesta-teal-500" />
              KPIs
            </CardTitle>
            {!isReadOnly && (
              <Button variant="outline" size="sm" onClick={handleAddKpi}>
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {kpis.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">
              No KPIs yet.{!isReadOnly && ' Add measurable outcomes to track project success.'}
            </p>
          ) : (
            <div className="space-y-3">
              {kpis.map((kpi) => (
                <KpiItem
                  key={kpi.id}
                  kpi={kpi}
                  history={kpiHistory.get(kpi.id) ?? []}
                  onUpdate={handleUpdateKpi}
                  onDelete={handleDeleteKpi}
                  onAddHistoryPoint={handleAddKpiHistoryPoint}
                  onUpdateHistoryPoint={handleUpdateKpiHistoryPoint}
                  onDeleteHistoryPoint={handleDeleteKpiHistoryPoint}
                  readOnly={isReadOnly}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tasks — embedded task list + AI task generation at the bottom of the
          Details tab, alongside the KPIs. */}
      <Card id="section-tasks" className="scroll-mt-4">
        <CardContent className="pt-6">
          <ProjectTasks embedded onTasksChanged={setTasks} />
        </CardContent>
      </Card>

      {/* Danger Zone - Only show for admins */}
      {!isReadOnly && (
        <Card className="border-red-200 bg-red-50/50">
          <CardHeader>
            <CardTitle className="text-red-900 flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Danger Zone
            </CardTitle>
            <CardDescription className="text-red-700">
              Irreversible actions that permanently affect this project
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-red-200">
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Delete Project</h3>
                <p className="text-sm text-gray-600">
                  Permanently delete this project and all associated data.
                </p>
              </div>
              <Button
                variant="destructive"
                onClick={() => setShowDeleteDialog(true)}
                className="ml-4"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
        </>
      )}

      {activeTab === 'documents' && id && (
        <ProjectDocumentsSection
          projectId={id}
          isReadOnly={isReadOnly}
          onRegenerateClick={isDraft ? undefined : () => setShowRegenerateDialog(true)}
        />
      )}

      {/* Regenerate-from-documents Dialog */}
      {id && (
        <RegenerateProjectDialog
          open={showRegenerateDialog}
          onOpenChange={setShowRegenerateDialog}
          projectId={id}
          workspaceId={currentWorkspace?.id}
          onApplied={async () => {
            // Force a full reload of the project so the dashboard and
            // Details panes pick up the wipe-and-rewrite changes.
            setReloadKey((k) => k + 1);
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Project</DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete the project
              and all associated milestones, KPIs, and data.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm">
              Please type <span className="font-semibold">delete {projectName}</span> to confirm.
            </p>
            <Input
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              placeholder={`delete ${projectName}`}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setDeleteConfirmation('');
              }}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteProject}
              disabled={deleteConfirmation !== `delete ${projectName}` || isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete Project'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discard Draft Dialog — lighter than the delete dialog above since
          a draft is the user's own un-promoted work. No name re-typing. */}
      <Dialog open={showDiscardDraftDialog} onOpenChange={setShowDiscardDraftDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Discard this draft?</DialogTitle>
            <DialogDescription>
              The draft project, including any uploaded files, will be permanently deleted.
              This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDiscardDraftDialog(false)}
              disabled={isDeleting}
            >
              Keep editing
            </Button>
            <Button
              variant="destructive"
              onClick={handleDiscardDraft}
              disabled={isDeleting}
            >
              {isDeleting ? 'Discarding…' : 'Discard draft'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regenerate Summary Review Dialog — opens when the user clicks
          Regenerate on the Dashboard tab. Shows the current summary, the
          newly generated summary side-by-side (or stacked on narrower
          viewports), and lets the user explicitly accept or discard.
          Closing the dialog mid-generation simply discards the result
          when it lands; nothing in the project is touched until the user
          clicks "Use new summary". */}
      <Dialog open={summaryReviewOpen} onOpenChange={(open) => !open && cancelSummaryReview()}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Edit executive summary</DialogTitle>
            <DialogDescription>
              Compare against the current summary on the left. On the right, click
              Generate now to have AI write one for you, or type your own. You
              can edit either way before saving.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            {/* Current — read-only reference. Header row uses h-9 so it
                lines up with the right column's button-bearing header,
                keeping both bodies top-aligned regardless of which side
                has UI in the header. */}
            <div className="space-y-2">
              <div className="flex items-center h-9">
                <h3 className="text-xs font-medium uppercase tracking-wider text-gray-500">
                  Current
                </h3>
              </div>
              <div className="rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-800 leading-relaxed h-[280px] overflow-y-auto whitespace-pre-wrap">
                {summaryReviewOldText.trim()
                  ? summaryReviewOldText
                  : <span className="text-gray-400 italic">No summary yet.</span>}
              </div>
            </div>

            {/* Generated — editable. Generate now button populates the
                textarea with fresh AI output; the user can also type from
                scratch or edit AI output before saving. Header row matches
                the left column's h-9; textarea is fixed-height so both
                boxes align top and bottom (resize-none kills the drag
                handle, content scrolls when it overflows). */}
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2 h-9">
                <h3 className="text-xs font-medium uppercase tracking-wider text-ingesta-blue-700 flex items-center gap-1.5">
                  <Sparkles className="h-3 w-3" />
                  Generated
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={generateSummaryNow}
                  disabled={summaryReviewStatus === 'generating'}
                  className="bg-white"
                >
                  {summaryReviewStatus === 'generating' ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      Generating…
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                      {summaryReviewNewText ? 'Regenerate' : 'Generate now'}
                    </>
                  )}
                </Button>
              </div>
              <textarea
                value={summaryReviewNewText}
                onChange={(e) => setSummaryReviewNewText(e.target.value)}
                disabled={summaryReviewStatus === 'generating'}
                placeholder="Click Generate now to have AI write a summary, or type your own here."
                className="w-full rounded-md border border-ingesta-blue-200 bg-ingesta-blue-50 p-4 text-sm text-gray-900 leading-relaxed h-[280px] resize-none overflow-y-auto disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-ingesta-blue-400"
              />
              {summaryReviewStatus === 'error' && summaryReviewError && (
                <p className="text-xs text-red-600">{summaryReviewError}</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={cancelSummaryReview}>
              Cancel
            </Button>
            <Button
              onClick={saveSummaryChanges}
              disabled={summaryReviewStatus === 'generating'}
            >
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
